import React from 'react'
import { Wallet, ChevronDown, Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useWalletManager } from '@/lib/store/wallet-manager'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { cn } from '@/lib/utils'

export function WalletSelector() {
  const navigate = useNavigate()
  const { wallets, activeWalletId, setActiveWallet, getActiveWallet } = useWalletManager()
  const activeWallet = getActiveWallet()
  
  const handleWalletSelect = (walletId: string) => {
    setActiveWallet(walletId)
    navigate(`/hd-wallet?id=${walletId}`)
  }
  
  const handleViewAll = () => {
    navigate('/wallets')
  }
  
  const handleAddWallet = () => {
    navigate('/hd-wallet?new=true')
  }
  
  // Don't show if no wallets
  if (wallets.length === 0) {
    return null
  }
  
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
          <Wallet className="w-4 h-4" />
          <span className="max-w-[150px] truncate">
            {activeWallet?.name || 'Select Wallet'}
          </span>
          <ChevronDown className="w-4 h-4" />
        </button>
      </DropdownMenu.Trigger>
      
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="min-w-[220px] bg-white dark:bg-gray-800 rounded-md p-1 shadow-lg z-50"
          sideOffset={5}
        >
          {/* Wallet list */}
          {wallets.map((wallet) => (
            <DropdownMenu.Item
              key={wallet.id}
              className={cn(
                "flex items-center justify-between px-3 py-2 text-sm rounded cursor-pointer outline-none",
                "hover:bg-gray-100 dark:hover:bg-gray-700",
                wallet.id === activeWalletId && "bg-quantum-50 dark:bg-quantum-900/20"
              )}
              onSelect={() => handleWalletSelect(wallet.id)}
            >
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                <span className="truncate">{wallet.name}</span>
              </div>
              {wallet.id === activeWalletId && (
                <div className="w-2 h-2 bg-quantum-600 rounded-full" />
              )}
            </DropdownMenu.Item>
          ))}
          
          <DropdownMenu.Separator className="h-[1px] bg-gray-200 dark:bg-gray-700 my-1" />
          
          {/* View all wallets */}
          <DropdownMenu.Item
            className="flex items-center gap-2 px-3 py-2 text-sm rounded cursor-pointer outline-none hover:bg-gray-100 dark:hover:bg-gray-700"
            onSelect={handleViewAll}
          >
            <Wallet className="w-4 h-4" />
            View All Wallets
          </DropdownMenu.Item>
          
          {/* Add new wallet */}
          <DropdownMenu.Item
            className="flex items-center gap-2 px-3 py-2 text-sm rounded cursor-pointer outline-none hover:bg-gray-100 dark:hover:bg-gray-700"
            onSelect={handleAddWallet}
          >
            <Plus className="w-4 h-4" />
            Add New Wallet
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}